// src/diagrams/ContactFlowNodes.js - Custom node components for React Flow with individual labeled handles

import React, { memo } from 'react';
import { Handle, Position, NodeToolbar } from '@xyflow/react';

// Base styles for all nodes
const baseNodeStyle = {
  border: '2px solid',
  borderRadius: '8px',
  background: 'white',
  padding: '12px',
  fontSize: '12px',
  fontFamily: 'Arial, sans-serif',
  minWidth: '160px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  cursor: 'pointer',
};

// Category-based color schemes
export const categoryColors = {
  communication: { border: '#2196F3', bg: '#E3F2FD', icon: '#1976D2' },
  input: { border: '#9C27B0', bg: '#F3E5F5', icon: '#7B1FA2' },
  integration: { border: '#FF9800', bg: '#FFF3E0', icon: '#F57C00' },
  routing: { border: '#4CAF50', bg: '#E8F5E8', icon: '#388E3C' },
  validation: { border: '#FF9800', bg: '#FFF3E0', icon: '#F57C00' },
  terminator: { border: '#F44336', bg: '#FFEBEE', icon: '#D32F2F' },
  recording: { border: '#795548', bg: '#EFEBE9', icon: '#5D4037' },
  data: { border: '#607D8B', bg: '#ECEFF1', icon: '#455A64' },
  transfer: { border: '#009688', bg: '#E0F2F1', icon: '#00695C' },
  system: { border: '#9E9E9E', bg: '#F5F5F5', icon: '#616161' },
  callback: { border: '#673AB7', bg: '#EDE7F6', icon: '#512DA8' },
  general: { border: '#666666', bg: '#F5F5F5', icon: '#424242' }
};

// Helper function to create labeled handles
const createLabeledHandle = (transition, index, totalCount, position, layoutDirection, colors) => {
  const isHorizontal = layoutDirection === 'LR';
  
  // Calculate handle position along the edge
  const spacing = 100 / (totalCount + 1);
  const positionPercentage = spacing * (index + 1);
  
  // Determine handle color based on transition type
  const handleColor = transition.type === 'error' ? '#f44336' : 
                    transition.type === 'condition' ? '#2196f3' : 
                    transition.type === 'success' ? '#4CAF50' : 
                    colors.border;
  
  if (isHorizontal) {
    // For horizontal layout, handles on right edge
    return (
      <React.Fragment key={transition.handleId}>
        <Handle
          type="source"
          position={position}
          id={transition.handleId}
          style={{
            position: 'absolute',
            top: `${positionPercentage}%`,
            right: '-6px',
            transform: 'translateY(-50%)',
            background: handleColor,
            border: 'none',
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            zIndex: 10
          }}
        />
        {transition.label && (
          <div style={{
            position: 'absolute',
            top: `${positionPercentage}%`,
            left: '100%',
            marginLeft: '14px', // 6px for handle offset + 8px spacing
            transform: 'translateY(-50%) rotate(-45deg)',
            fontSize: '9px',
            whiteSpace: 'nowrap',
            background: 'white',
            padding: '2px 4px',
            borderRadius: '3px',
            border: `1px solid ${handleColor}`,
            color: handleColor,
            fontWeight: '500',
            pointerEvents: 'none',
            zIndex: 100,
            transformOrigin: 'left center'
          }}>
            {transition.label}
          </div>
        )}
      </React.Fragment>
    );
  } else {
    // For vertical layout, handles on bottom edge
    return (
      <React.Fragment key={transition.handleId}>
        <Handle
          type="source"
          position={position}
          id={transition.handleId}
          style={{
            position: 'absolute',
            left: `${positionPercentage}%`,
            bottom: '-6px',
            transform: 'translateX(-50%)',
            background: handleColor,
            border: 'none',
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            zIndex: 10
          }}
        />
        {transition.label && (
          <div style={{
            position: 'absolute',
            left: `${positionPercentage}%`,
            top: '100%',
            marginTop: '14px', // 6px for handle offset + 8px spacing
            transform: 'translateX(-50%) rotate(-45deg)',
            fontSize: '9px',
            whiteSpace: 'nowrap',
            background: 'white',
            padding: '2px 4px',
            borderRadius: '3px',
            border: `1px solid ${handleColor}`,
            color: handleColor,
            fontWeight: '500',
            pointerEvents: 'none',
            zIndex: 100,
            transformOrigin: 'top center'
          }}>
            {transition.label}
          </div>
        )}
      </React.Fragment>
    );
  }
};

// Entry Node Component
export const EntryNode = memo(({ id, data, selected }) => {
  const colors = categoryColors.system;
  const layoutDirection = data.layoutDirection || 'TB';
  const sourcePosition = layoutDirection === 'LR' ? Position.Right : Position.Bottom;
  
  const nodeStyle = {
    ...baseNodeStyle,
    borderColor: colors.border,
    background: colors.bg,
    borderRadius: '50%',
    width: '80px',
    height: '80px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '8px',
    borderWidth: '3px',
    position: 'relative'
  };

  const transitions = data.individualTransitions || [];

  return (
    <div style={nodeStyle} className="node-drag-handle">
      <NodeToolbar isVisible={selected} position={Position.Top}>
        <div style={{ 
          background: 'white', 
          border: '1px solid #ccc', 
          borderRadius: '4px', 
          padding: '8px',
          fontSize: '11px',
          maxWidth: '200px'
        }}>
          <strong>Flow Entry Point</strong><br/>
          This is where the contact flow begins when triggered.
        </div>
      </NodeToolbar>
      
      <div style={{ fontSize: '18px', marginBottom: '2px' }}>
        {data.icon}
      </div>
      <div style={{ fontSize: '10px', fontWeight: 'bold', color: colors.icon }}>
        ENTRY
      </div>
      
      {/* Render individual handles */}
      {transitions.map((transition, index) => 
        createLabeledHandle(transition, index, transitions.length, sourcePosition, layoutDirection, colors)
      )}
    </div>
  );
});

// Start Node Component
export const StartNode = memo(({ id, data, selected }) => {
  const colors = categoryColors[data.category] || categoryColors.general;
  const layoutDirection = data.layoutDirection || 'TB';
  const sourcePosition = layoutDirection === 'LR' ? Position.Right : Position.Bottom;
  const targetPosition = layoutDirection === 'LR' ? Position.Left : Position.Top;
  
  const transitions = data.individualTransitions || [];
  
  const nodeStyle = {
    ...baseNodeStyle,
    borderColor: colors.border,
    background: colors.bg,
    borderRadius: '50%',
    width: '100px',
    height: '100px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '8px',
    position: 'relative'
  };

  return (
    <div style={nodeStyle} className="node-drag-handle">
      <NodeToolbar isVisible={selected} position={Position.Top}>
        <div style={{ 
          background: 'white', 
          border: '1px solid #ccc', 
          borderRadius: '4px', 
          padding: '8px',
          fontSize: '11px',
          maxWidth: '200px'
        }}>
          <strong>Start Node</strong><br/>
          {data.description}<br/>
          <em>Type: {data.actionType}</em>
          {data.metadata.hasErrors && <><br/><span style={{color: '#f44336'}}>Has error handling</span></>}
          {data.metadata.hasConditions && <><br/><span style={{color: '#2196F3'}}>Has conditions</span></>}
        </div>
      </NodeToolbar>
      
      <Handle
        type="target"
        position={targetPosition}
        style={{ 
          background: colors.border, 
          border: 'none',
          [layoutDirection === 'LR' ? 'left' : 'top']: '-8px'
        }}
      />
      
      <div style={{ fontSize: '20px', marginBottom: '4px' }}>
        {data.icon}
      </div>
      <div style={{ fontSize: '9px', marginTop: '2px' }}>
        {data.label}
      </div>
      
      {/* Render individual handles with labels */}
      {transitions.map((transition, index) => 
        createLabeledHandle(transition, index, transitions.length, sourcePosition, layoutDirection, colors)
      )}
    </div>
  );
});

// End Node Component
export const EndNode = memo(({ id, data, selected }) => {
  const colors = categoryColors.terminator;
  const layoutDirection = data.layoutDirection || 'TB';
  const targetPosition = layoutDirection === 'LR' ? Position.Left : Position.Top;
  
  const nodeStyle = {
    ...baseNodeStyle,
    borderColor: colors.border,
    background: colors.bg,
    borderRadius: '50%',
    width: '100px',
    height: '100px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '8px',
    position: 'relative'
  };

  return (
    <div style={nodeStyle} className="node-drag-handle">
      <NodeToolbar isVisible={selected} position={Position.Top}>
        <div style={{ 
          background: 'white', 
          border: '1px solid #ccc', 
          borderRadius: '4px', 
          padding: '8px',
          fontSize: '11px',
          maxWidth: '200px'
        }}>
          <strong>End Node</strong><br/>
          {data.description}<br/>
          <em>Type: {data.actionType}</em>
        </div>
      </NodeToolbar>
      
      <Handle
        type="target"
        position={targetPosition}
        style={{ 
          background: colors.border, 
          border: 'none',
          [layoutDirection === 'LR' ? 'left' : 'top']: '-8px'
        }}
      />
      
      <div style={{ fontSize: '20px', marginBottom: '4px' }}>
        {data.icon}
      </div>
      <div style={{ fontSize: '10px', fontWeight: 'bold', color: colors.icon }}>
        END
      </div>
      <div style={{ fontSize: '9px', marginTop: '2px' }}>
        {data.label}
      </div>
    </div>
  );
});

// Process Node Component
export const ProcessNode = memo(({ id, data, selected }) => {
  const colors = categoryColors[data.category] || categoryColors.general;
  const display = data.displayInfo || {};
  const layoutDirection = data.layoutDirection || 'TB';
  const sourcePosition = layoutDirection === 'LR' ? Position.Right : Position.Bottom;
  const targetPosition = layoutDirection === 'LR' ? Position.Left : Position.Top;
  
  const transitions = data.individualTransitions || [];
  
  // Calculate dynamic width based on content
  let minWidth = 180;
  if (data.actionType === 'UpdateContactAttributes' && display.attributeCount > 0) {
    // For UpdateContactAttributes, calculate width based on longest attribute with minimal padding
    const maxAttributeLength = Math.max(
      ...Object.entries(display.attributes).map(([key, value]) => 
        (key + ': ' + String(value)).length
      )
    );
    minWidth = Math.max(200, Math.min(320, maxAttributeLength * 5 + 60)); // Further reduced multiplier and padding
  } else if (data.actionType === 'MessageParticipantIteratively') {
    if (display.messageCount > 0) {
      // Calculate width based on longest message content
      const maxMessageLength = Math.max(
        ...display.messages.map(msg => msg.content.length)
      );
      minWidth = Math.max(220, Math.min(400, maxMessageLength * 4 + 120));
    }
  } else if (data.actionType === 'Loop') {
    // Loop nodes are simple, just need basic width
    minWidth = 180;
  } else if (display.displayMessage || display.displayPrompt || display.displaySSML) {
    const textLength = (display.displayMessage || display.displayPrompt || display.displaySSML || '').length;
    minWidth = Math.max(180, Math.min(450, textLength * 4 + 120));
  } else if (display.promptName) {
    const textLength = display.promptName.length;
    minWidth = Math.max(180, Math.min(350, textLength * 8 + 120));
  } else if (display.mediaUri) {
    const textLength = display.mediaUri.length;
    minWidth = Math.max(180, Math.min(400, textLength * 6 + 120));
  }
  
  const nodeStyle = {
    ...baseNodeStyle,
    borderColor: colors.border,
    background: colors.bg,
    minHeight: data.actionType === 'GetParticipantInput' ? '100px' : '80px',
    minWidth: `${minWidth}px`,
    maxWidth: '450px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    position: 'relative'
  };

  const renderContent = () => {
    switch (data.actionType) {
      case 'MessageParticipant':
        if (display.contentType === 'text' && display.displayMessage) {
          return (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px', lineHeight: '1.3' }}>
              "{display.displayMessage}"
            </div>
          );
        } else if (display.contentType === 'ssml' && display.displaySSML) {
          return (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px', lineHeight: '1.3' }}>
              <div style={{ fontSize: '9px', color: '#666', marginBottom: '2px' }}>SSML:</div>
              "{display.displaySSML}"
            </div>
          );
        } else if (display.contentType === 'prompt' && display.promptName) {
          return (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px', lineHeight: '1.3' }}>
              <div style={{ fontSize: '9px', color: '#666', marginBottom: '2px' }}>Prompt:</div>
              {display.promptName}
            </div>
          );
        } else if (display.contentType === 'media' && display.mediaUri) {
          return (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px', lineHeight: '1.3' }}>
              <div style={{ fontSize: '9px', color: '#666', marginBottom: '2px' }}>Media:</div>
              {display.mediaUri}
            </div>
          );
        }
        break;
        
      case 'GetParticipantInput':
        let content = null;
        
        if (display.contentType === 'text' && display.displayPrompt) {
          content = (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px', lineHeight: '1.3' }}>
              "{display.displayPrompt}"
            </div>
          );
        } else if (display.contentType === 'ssml' && display.displaySSML) {
          content = (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px', lineHeight: '1.3' }}>
              <div style={{ fontSize: '9px', color: '#666', marginBottom: '2px' }}>SSML:</div>
              "{display.displaySSML}"
            </div>
          );
        } else if (display.contentType === 'prompt' && display.promptName) {
          content = (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px', lineHeight: '1.3' }}>
              <div style={{ fontSize: '9px', color: '#666', marginBottom: '2px' }}>Prompt:</div>
              {display.promptName}
            </div>
          );
        } else if (display.contentType === 'media' && display.mediaUri) {
          content = (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px', lineHeight: '1.3' }}>
              <div style={{ fontSize: '9px', color: '#666', marginBottom: '2px' }}>Media:</div>
              {display.mediaUri}
            </div>
          );
        }
        
        return (
          <div>
            {content}
            {display.timeout && (
              <div style={{ fontSize: '9px', color: '#666', marginTop: '2px' }}>
                Timeout: {display.timeout}s
              </div>
            )}
            {display.storeInput && (
              <div style={{ fontSize: '9px', color: '#4CAF50', marginTop: '2px' }}>
                ✓ Stores Input
              </div>
            )}
          </div>
        );

      case 'ConnectParticipantWithLexBot':
        let lexContent = null;
        
        // Display prompt content
        if (display.contentType === 'text' && display.displayPrompt) {
          lexContent = (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px', lineHeight: '1.3' }}>
              "{display.displayPrompt}"
            </div>
          );
        } else if (display.contentType === 'ssml' && display.displaySSML) {
          lexContent = (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px', lineHeight: '1.3' }}>
              <div style={{ fontSize: '9px', color: '#666', marginBottom: '2px' }}>SSML:</div>
              "{display.displaySSML}"
            </div>
          );
        } else if (display.contentType === 'prompt' && display.promptName) {
          lexContent = (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px', lineHeight: '1.3' }}>
              <div style={{ fontSize: '9px', color: '#666', marginBottom: '2px' }}>Prompt:</div>
              {display.promptName}
            </div>
          );
        }
        
        return (
          <div>
            {/* Display prompt content */}
            {lexContent}
            {/* Display timeout */}
            {display.timeout && (
              <div style={{ fontSize: '9px', color: '#666', marginTop: '2px' }}>
                Timeout: {display.timeout}s
              </div>
            )}
          </div>
        );
        
      case 'InvokeFlowModule':
        if (display.flowModuleName) {
          return (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px' }}>
              {display.flowModuleName}
            </div>
          );
        }
        break;
        
      case 'CompleteOutboundCall':
        if (display.phoneNumber) {
          return (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px' }}>
              Number: {display.phoneNumber}
            </div>
          );
        }
        break;
        
      case 'UpdateContactTextToSpeechVoice':
        return (
          <div style={{ fontSize: '10px', color: '#333', marginTop: '4px', lineHeight: '1.3' }}>
            {display.textToSpeechVoice && (
              <div style={{ marginBottom: '2px' }}>
                <strong>Voice:</strong> {display.textToSpeechVoice}
              </div>
            )}
            {display.textToSpeechEngine && (
              <div style={{ marginBottom: '2px' }}>
                <strong>Engine:</strong> {display.textToSpeechEngine}
              </div>
            )}
            {display.textToSpeechStyle && (
              <div style={{ marginBottom: '2px' }}>
                <strong>Style:</strong> {display.textToSpeechStyle}
              </div>
            )}
          </div>
        );
        
      case 'TransferParticipantToThirdParty':
        if (display.phoneNumber) {
          return (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px' }}>
              {display.phoneNumber}
              {display.timeout && (
                <div style={{ fontSize: '9px', color: '#666', marginTop: '2px' }}>
                  Timeout: {display.timeout}s
                </div>
              )}
            </div>
          );
        }
        break;
        
      case 'InvokeLambdaFunction':
        if (display.functionName) {
          return (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px' }}>
              {display.functionName}
              {display.timeout && (
                <div style={{ fontSize: '9px', color: '#666', marginTop: '2px' }}>
                  Timeout: {display.timeout}s
                </div>
              )}
              {display.lambdaInvocationAttributes && (
                <div style={{ fontSize: '9px', color: '#333', marginTop: '4px' }}>
                  <div style={{ fontSize: '8px', color: '#666', marginBottom: '2px' }}>
                    Invocation Attributes ({display.attributeCount}):
                  </div>
                  {Object.entries(display.lambdaInvocationAttributes).slice(0, 3).map(([key, value]) => (
                    <div key={key} style={{ fontSize: '8px', marginBottom: '1px', wordBreak: 'break-word' }}>
                      <strong>{key}:</strong> {String(value)}
                    </div>
                  ))}
                  {display.attributeCount > 3 && (
                    <div style={{ fontSize: '8px', color: '#666' }}>
                      +{display.attributeCount - 3} more...
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        }
        break;
        
      case 'UpdateContactAttributes':
        if (display.attributeCount > 0) {
          return (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px' }}>
              {Object.keys(display.attributes).slice(0, 3).map(key => (
                <div key={key} style={{ fontSize: '9px', marginBottom: '1px', wordBreak: 'break-word' }}>
                  <strong>{key}:</strong> {String(display.attributes[key])}
                </div>
              ))}
              {display.attributeCount > 3 && (
                <div style={{ fontSize: '9px', color: '#666' }}>
                  +{display.attributeCount - 3} more...
                </div>
              )}
            </div>
          );
        }
        break;
        
      case 'UpdateContactRecordingBehavior':
        if (display.recordingParticipants) {
          const participants = display.recordingParticipants;
          return (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px' }}>
              {participants.length === 0 && 'No recording'}
              {participants.includes('Agent') && participants.includes('Customer') && 'Agent + Customer'}
              {participants.includes('Agent') && !participants.includes('Customer') && 'Agent only'}
              {!participants.includes('Agent') && participants.includes('Customer') && 'Customer only'}
            </div>
          );
        }
        break;
        
      case 'CreateCallbackContact':
        return (
          <div style={{ fontSize: '10px', color: '#333', marginTop: '4px' }}>
            {display.targetQueue && <div>Queue: {display.targetQueue}</div>}
            <div style={{ fontSize: '9px', color: '#666' }}>
              Delay: {display.initialDelay || 0}s, Max: {display.maxAttempts || 1}
            </div>
          </div>
        );
      case 'UpdateContactRoutingBehavior':
        if (display.routingType === 'priority') {
          return (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px' }}>
              <div style={{ fontSize: '9px', color: '#666', marginBottom: '2px' }}>Queue Priority:</div>
              <div style={{ fontWeight: 'bold' }}>{display.queuePriority}</div>
            </div>
          );
        } else if (display.routingType === 'timeAdjustment') {
          return (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px' }}>
              <div style={{ fontSize: '9px', color: '#666', marginBottom: '2px' }}>Time Adjustment:</div>
              <div style={{ fontWeight: 'bold' }}>{display.queueTimeAdjustment}s</div>
            </div>
          );
        }
        break;

      case 'ShowView':
        return (
          <div style={{ fontSize: '10px', color: '#333', marginTop: '4px' }}>
            {display.viewId && (
              <div style={{ marginBottom: '2px' }}>
                <div style={{ fontSize: '9px', color: '#666' }}>View ID:</div>
                <div style={{ fontWeight: 'bold' }}>{display.viewId}</div>
              </div>
            )}
            {display.timeout && (
              <div style={{ fontSize: '9px', color: '#666' }}>
                Timeout: {display.timeout}s
              </div>
            )}
          </div>
        );

      case 'Wait':
        return (
          <div style={{ fontSize: '10px', color: '#333', marginTop: '4px' }}>
            <div style={{ fontSize: '9px', color: '#666', marginBottom: '2px' }}>Wait Duration:</div>
            <div style={{ fontWeight: 'bold' }}>{display.timeoutSeconds}s</div>
          </div>
        );

      case 'Loop':
        return (
          <div style={{ fontSize: '10px', color: '#333', marginTop: '4px' }}>
            <div style={{ fontSize: '9px', color: '#666', marginBottom: '2px' }}>Loop Count:</div>
            <div style={{ fontWeight: 'bold' }}>{display.loopCount}x</div>
          </div>
        );

      case 'MessageParticipantIteratively':
        if (display.messages && display.messages.length > 0) {
          return (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px' }}>
              <div style={{ fontSize: '9px', color: '#666', marginBottom: '4px' }}>
                Messages ({display.messageCount}):
              </div>
              {display.messages.slice(0, 3).map((message, index) => (
                <div key={index} style={{ fontSize: '9px', marginBottom: '2px', lineHeight: '1.3' }}>
                  <span style={{ color: '#666' }}>{message.index}.</span>
                  {message.contentType === 'text' && (
                    <span style={{ marginLeft: '4px' }}>"{message.content}"</span>
                  )}
                  {message.contentType === 'ssml' && (
                    <span style={{ marginLeft: '4px' }}>
                      <span style={{ color: '#666' }}>SSML:</span> "{message.content}"
                    </span>
                  )}
                  {message.contentType === 'prompt' && (
                    <span style={{ marginLeft: '4px' }}>
                      <span style={{ color: '#666' }}>Prompt:</span> {message.content}
                    </span>
                  )}
                  {message.contentType === 'media' && (
                    <span style={{ marginLeft: '4px' }}>
                      <span style={{ color: '#666' }}>Media:</span> {message.content}
                    </span>
                  )}
                </div>
              ))}
              {display.messages.length > 3 && (
                <div style={{ fontSize: '9px', color: '#666' }}>
                  +{display.messages.length - 3} more...
                </div>
              )}
            </div>
          );
        }
        break;

      case 'UpdatePreviousContactParticipantState':
        if (display.displayState) {
          return (
            <div style={{ fontSize: '10px', color: '#333', marginTop: '4px' }}>
              <div style={{ fontSize: '9px', color: '#666', marginBottom: '2px' }}>Action:</div>
              <div style={{ fontWeight: 'bold' }}>{display.displayState}</div>
            </div>
          );
        }
        break;
      
      case 'CheckMetricData':
        return (
          <div style={{ fontSize: '10px', color: '#333', marginTop: '4px' }}>
            {display.metricType && (
              <div style={{ fontSize: '9px', color: '#666', marginBottom: '2px' }}>
                Metric: <strong>{display.metricType}</strong>
              </div>
            )}
            {display.queueName && (
              <div style={{ fontSize: '9px', marginBottom: '1px' }}>
                Queue: {display.queueName}
              </div>
            )}
            {display.agentName && (
              <div style={{ fontSize: '9px', marginBottom: '1px' }}>
                Agent: {display.agentName}
              </div>
            )}
            {display.statistic && display.threshold && display.operator && (
              <div style={{ fontSize: '9px', marginTop: '2px' }}>
                {display.statistic} {display.operator} {display.threshold}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
    
    return null;
  };

  return (
    <div style={nodeStyle} className="node-drag-handle">
      <NodeToolbar isVisible={selected} position={Position.Top}>
        <div style={{ 
          background: 'white', 
          border: '1px solid #ccc', 
          borderRadius: '4px', 
          padding: '8px',
          fontSize: '11px',
          maxWidth: '300px'
        }}>
          <strong>{data.label}</strong><br/>
          {data.description}<br/>
          <em>Type: {data.actionType}</em><br/>
          {data.metadata.hasErrors && <span style={{color: '#f44336'}}>Has error handling</span>}<br/>
          {data.metadata.hasConditions && <span style={{color: '#2196F3'}}>Has conditions</span>}
          
          {/* Tooltip content */}
          {display.fullMessage && display.fullMessage !== display.displayMessage && (
            <>
              <br/><strong>Full Message:</strong> {display.fullMessage}
            </>
          )}
          {display.fullPrompt && display.fullPrompt !== display.displayPrompt && (
            <>
              <br/><strong>Full Prompt:</strong> {display.fullPrompt}
            </>
          )}
          {display.targetFlowId && (
            <>
              <br/><strong>Target Flow ID:</strong> {display.targetFlowId.split('/').pop()}
            </>
          )}
          {display.functionArn && (
            <>
              <br/><strong>Lambda ARN:</strong> {display.functionArn.split(':').slice(-2).join(':')}
              {display.lambdaInvocationAttributes && (
                <>
                  <br/><strong>Invocation Attributes ({display.attributeCount}):</strong>
                  {Object.entries(display.lambdaInvocationAttributes).map(([key, value]) => (
                    <div key={key} style={{ fontSize: '10px', marginLeft: '8px' }}>
                      <strong>{key}:</strong> {String(value)}
                    </div>
                  ))}
                </>
              )}
            </>
          )}
          {data.actionType === 'UpdateContactRoutingBehavior' && (
            <>
              {display.routingType === 'priority' && (
                <>
                  <br/><strong>Queue Priority:</strong> {display.queuePriority}
                </>
              )}
              {display.routingType === 'timeAdjustment' && (
                <>
                  <br/><strong>Time Adjustment:</strong> {display.queueTimeAdjustment}s
                </>
              )}
            </>
          )}

          {data.actionType === 'ShowView' && (
            <>
              {display.viewId && (
                <>
                  <br/><strong>View ID:</strong> {display.viewId}
                </>
              )}
              {display.viewVersion && (
                <>
                  <br/><strong>View Version:</strong> {display.viewVersion}
                </>
              )}
              <br/><strong>Timeout:</strong> {display.timeout}s
            </>
          )}

          {data.actionType === 'Wait' && (
            <>
              <br/><strong>Wait Duration:</strong> {display.timeoutSeconds}s
            </>
          )}

          {data.actionType === 'Loop' && (
            <>
              <br/><strong>Loop Count:</strong> {display.loopCount}x
            </>
          )}

          {data.actionType === 'MessageParticipantIteratively' && display.messages && (
            <>
              <br/><strong>Message Count:</strong> {display.messageCount}
              {display.messages.map((message, index) => (
                <div key={index} style={{ fontSize: '10px', marginLeft: '8px' }}>
                  {message.index}. {message.contentType === 'text' && `"${message.fullContent}"`}
                  {message.contentType === 'ssml' && `SSML: "${message.fullContent}"`}
                  {message.contentType === 'prompt' && `Prompt: ${message.fullContent}`}
                  {message.contentType === 'media' && `Media: ${message.fullContent}`}
                </div>
              ))}
            </>
          )}

          {data.actionType === 'UpdatePreviousContactParticipantState' && display.participantState && (
            <>
              <br/><strong>Participant State:</strong> {display.participantState}
              <br/><strong>Action:</strong> {display.displayState}
            </>
          )}

          {data.actionType === 'ConnectParticipantWithLexBot' && (
            <>
              {display.lexBotInfo && (
                <>
                  <br/><strong>{display.lexBotType === 'v2' ? 'Lex V2 Bot' : 'Lex Bot'}:</strong> {display.lexBotInfo.name} ({display.lexBotInfo.alias})
                  {display.lexBotType === 'v2' && display.lexBotInfo.botId && (
                    <>
                      <br/><strong>Bot ID:</strong> {display.lexBotInfo.botId}
                    </>
                  )}
                  {display.lexBotType === 'v1' && display.lexBotInfo.region && (
                    <>
                      <br/><strong>Region:</strong> {display.lexBotInfo.region}
                    </>
                  )}
                </>
              )}
              {display.fullPrompt && display.fullPrompt !== display.displayPrompt && (
                <>
                  <br/><strong>Full Prompt:</strong> {display.fullPrompt}
                </>
              )}
              <br/><strong>Timeout:</strong> {display.timeout}s
            </>
          )}
        </div>
      </NodeToolbar>
      
      <Handle
        type="target"
        position={targetPosition}
        style={{ 
          background: colors.border, 
          border: 'none',
          [layoutDirection === 'LR' ? 'left' : 'top']: '-8px'
        }}
      />
      
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontSize: '16px', marginRight: '8px' }}>{data.icon}</span>
        <div>
          <div style={{ fontWeight: 'bold', color: colors.icon, fontSize: '11px' }}>
            {data.label}
          </div>
          <div style={{ fontSize: '9px', color: '#666', marginTop: '2px' }}>
            {data.actionType.replace(/([A-Z])/g, ' $1').trim()}
          </div>
        </div>
      </div>
      
      {/* Content display */}
      {renderContent()}
      
      {/* Render individual handles with labels */}
      {transitions.map((transition, index) => 
        createLabeledHandle(transition, index, transitions.length, sourcePosition, layoutDirection, colors)
      )}
    </div>
  );
});

// Decision Node Component (Diamond shape)
export const DecisionNode = memo(({ id, data, selected }) => {
  const colors = categoryColors[data.category] || categoryColors.validation;
  const display = data.displayInfo || {};
  const layoutDirection = data.layoutDirection || 'TB';
  const targetPosition = layoutDirection === 'LR' ? Position.Left : Position.Top;
  
  const transitions = data.individualTransitions || [];
  
  // Adjust size based on content for Compare nodes
  let size = 120;
  if (data.actionType === 'Compare' && display.comparisonValue) {
    const valueLength = display.comparisonValue.length;
    size = Math.max(120, Math.min(180, valueLength * 6 + 120));
  }
  
  const diamondStyle = {
    width: `${size}px`,
    height: `${size}px`,
    background: colors.bg,
    border: `2px solid ${colors.border}`,
    transform: 'rotate(45deg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    cursor: 'pointer'
  };
  
  const contentStyle = {
    transform: 'rotate(-45deg)',
    textAlign: 'center',
    padding: '8px',
    fontSize: '10px',
    lineHeight: '1.2',
    maxWidth: `${size - 40}px`
  };

  // Calculate handle positions for decision nodes
const getDecisionHandlePositions = () => {
  const positions = [];
  
  transitions.forEach((transition, index) => {
    let position;
    let offsetStyle = {};
    
    if (layoutDirection === 'LR') {
      // For horizontal layout, only use right edge
      position = Position.Right;
      const spacing = 100 / (transitions.length + 1);
      const posIndex = index + 1;
      offsetStyle = {
        top: `${spacing * posIndex}%`,
        right: '-6px',
        transform: 'translateY(-50%) rotate(-45deg)',
        position: 'absolute'
      };
    } else {
      // For vertical layout, only use bottom edge
      position = Position.Bottom;
      const spacing = 100 / (transitions.length + 1);
      const posIndex = index + 1;
      offsetStyle = {
        left: `${spacing * posIndex}%`,
        bottom: '-6px',
        transform: 'translateX(-50%) rotate(-45deg)',
        position: 'absolute'
      };
    }
    
    positions.push({
      transition,
      position,
      offsetStyle
    });
  });
  
  return positions;
};

  const handlePositions = getDecisionHandlePositions();

  const renderDecisionContent = () => {
    if (data.actionType === 'Compare' && display.comparisonValue) {
      return (
        <div style={contentStyle}>
          <div style={{ fontSize: '14px', marginBottom: '4px' }}>
            {data.icon}
          </div>
          <div style={{ fontWeight: 'bold', color: colors.icon, fontSize: '9px', marginBottom: '2px' }}>
            COMPARE
          </div>
          <div style={{ fontSize: '8px', color: '#333', wordBreak: 'break-word' }}>
            {display.comparisonValue}
          </div>
        </div>
      );
    }
    
    if (data.actionType === 'CheckHoursOfOperation') {
      return (
        <div style={contentStyle}>
          <div style={{ fontSize: '14px', marginBottom: '2px' }}>
            {data.icon}
          </div>
          <div style={{ fontWeight: 'bold', color: colors.icon, fontSize: '9px', marginBottom: '2px' }}>
            HOURS
          </div>
          <div style={{ fontSize: '8px', color: '#333', wordBreak: 'break-word' }}>
            {display.hoursName || 'Current Queue'}
          </div>
        </div>
      );
    }
    
    return (
      <div style={contentStyle}>
        <div style={{ fontSize: '14px', marginBottom: '2px' }}>
          {data.icon}
        </div>
        <div style={{ fontWeight: 'bold', color: colors.icon, fontSize: '9px' }}>
          {data.label.split(' ').slice(0, 2).join(' ')}
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: 'relative' }}>
      <NodeToolbar isVisible={selected} position={Position.Top}>
        <div style={{ 
          background: 'white', 
          border: '1px solid #ccc', 
          borderRadius: '4px', 
          padding: '8px',
          fontSize: '11px',
          maxWidth: '300px'
        }}>
          <strong>{data.label}</strong><br/>
          {data.description}<br/>
          <em>Type: {data.actionType}</em><br/>
          
          {data.actionType === 'Compare' && display.comparisonValue && (
            <>
              <strong>Comparing:</strong> {display.comparisonValue}<br/>
            </>
          )}
          
          {data.actionType === 'CheckHoursOfOperation' && (
            <>
              <strong>Hours:</strong> {display.hoursName || 'Current Queue'}<br/>
              {display.hoursId && (
                <>
                  <strong>Hours ID:</strong> {display.hoursId.split('/').pop()}<br/>
                </>
              )}
            </>
          )}
          
          {data.actionType === 'CheckMetricData' && (
            <>
              {display.metricType && (
                <>
                  <strong>Metric Type:</strong> {display.metricType}<br/>
                </>
              )}
              {display.queueName && (
                <>
                  <strong>Queue:</strong> {display.queueName}<br/>
                </>
              )}
              {display.agentName && (
                <>
                  <strong>Agent:</strong> {display.agentName}<br/>
                </>
              )}
              {display.statistic && (
                <>
                  <strong>Statistic:</strong> {display.statistic}<br/>
                </>
              )}
              {display.operator && display.threshold && (
                <>
                  <strong>Condition:</strong> {display.statistic || 'Value'} {display.operator} {display.threshold}<br/>
                </>
              )}
            </>
          )}
          
          {display.conditions && display.conditions.length > 0 && (
            <>
              <strong>Conditions:</strong><br/>
              {display.conditions.map((condition, index) => (
                <div key={index} style={{ fontSize: '10px', marginLeft: '8px' }}>
                  • {condition.label} → {condition.nextAction}
                </div>
              ))}
            </>
          )}
          
          {data.metadata.hasConditions && (
            <>
              <strong>Branches:</strong> {data.metadata.conditionCount}<br/>
            </>
          )}
          
          {data.parameters.text && (
            <>
              <strong>Prompt:</strong> {data.parameters.text.substring(0, 100)}
              {data.parameters.text.length > 100 && '...'}
            </>
          )}
        </div>
      </NodeToolbar>
      
      <Handle
        type="target"
        position={targetPosition}
        style={{ 
          background: colors.border, 
          border: 'none',
          [layoutDirection === 'LR' ? 'left' : 'top']: '-8px'
        }}
      />
      
      <div style={diamondStyle} className="node-drag-handle">
        {renderDecisionContent()}
      </div>
      
      {/* Render individual handles with labels - special handling for diamond shape */}
      {handlePositions.map(({ transition, position, offsetStyle }, index) => {
        const handleColor = transition.type === 'error' ? '#f44336' : 
                           transition.type === 'condition' ? '#2196f3' : 
                           colors.border;
        
        // For diamond nodes, handles are already positioned by offsetStyle
        // We just need to position labels relative to those handles
        let labelStyle = {};
        
        if (position === Position.Right) {
          labelStyle = {
            position: 'absolute',
            top: offsetStyle.top,
            left: '100%',
            marginLeft: '20px', // Account for diamond rotation
            transform: 'translateY(-50%) rotate(-45deg)',
            transformOrigin: 'left center',
            fontSize: '9px',
            whiteSpace: 'nowrap',
            background: 'white',
            padding: '2px 4px',
            borderRadius: '3px',
            border: `1px solid ${handleColor}`,
            color: handleColor,
            fontWeight: '500',
            pointerEvents: 'none',
            zIndex: 100
          };
        } else if (position === Position.Bottom) {
          labelStyle = {
            position: 'absolute',
            left: offsetStyle.left,
            top: '100%',
            marginTop: '20px', // Account for diamond rotation
            transform: 'translateX(-50%) rotate(-45deg)',
            transformOrigin: 'top center',
            fontSize: '9px',
            whiteSpace: 'nowrap',
            background: 'white',
            padding: '2px 4px',
            borderRadius: '3px',
            border: `1px solid ${handleColor}`,
            color: handleColor,
            fontWeight: '500',
            pointerEvents: 'none',
            zIndex: 100
          };
        } else if (position === Position.Left) {
          labelStyle = {
            position: 'absolute',
            top: offsetStyle.top,
            right: '100%',
            marginRight: '20px', // Account for diamond rotation
            transform: 'translateY(-50%)',
            fontSize: '9px',
            whiteSpace: 'nowrap',
            background: 'white',
            padding: '2px 4px',
            borderRadius: '3px',
            border: `1px solid ${handleColor}`,
            color: handleColor,
            fontWeight: '500',
            pointerEvents: 'none',
            zIndex: 100
          };
        } else if (position === Position.Top) {
          labelStyle = {
            position: 'absolute',
            left: offsetStyle.left,
            bottom: '100%',
            marginBottom: '20px', // Account for diamond rotation
            transform: 'translateX(-50%)',
            fontSize: '9px',
            whiteSpace: 'nowrap',
            background: 'white',
            padding: '2px 4px',
            borderRadius: '3px',
            border: `1px solid ${handleColor}`,
            color: handleColor,
            fontWeight: '500',
            pointerEvents: 'none',
            zIndex: 100
          };
        }
        
        return (
          <React.Fragment key={transition.handleId}>
            <Handle
              type="source"
              position={position}
              id={transition.handleId}
              style={{ 
                background: handleColor, 
                border: 'none',
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                zIndex: 10,
                ...offsetStyle
              }}
            />
            {transition.label && (
              <div style={labelStyle}>
                {transition.label}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
});

// Export node types for React Flow
export const nodeTypes = {
  entryNode: EntryNode,
  startNode: StartNode,
  endNode: EndNode,
  processNode: ProcessNode,
  decisionNode: DecisionNode
};